import { useState } from 'react'
import { useTaskSections } from '../../hooks/useTaskSections'

export function TaskSectionsAdmin({ isDarkMode = false }) {
  const { sections, loading, createSection, updateSection, deleteSection } = useTaskSections()
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState('#6366f1')
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState('')
  const [draggedId, setDraggedId] = useState(null)
  const [dragOverId, setDragOverId] = useState(null)
  const [isSorting, setIsSorting] = useState(false)

  const handleCreate = async () => {
    if (!newName.trim()) {
      alert('Section name is required')
      return
    }
    try {
      await createSection(newName, newColor)
      setNewName('')
      setNewColor('#6366f1')
    } catch (err) {
      alert(`Failed to create: ${err.message}`)
    }
  }

  const handleEdit = async (section) => {
    if (!editName.trim()) {
      alert('Section name is required')
      return
    }
    try {
      await updateSection(section.id, { name: editName, color: editColor })
      setEditingId(null)
      setEditName('')
      setEditColor('')
    } catch (err) {
      alert(`Failed to update: ${err.message}`)
    }
  }

  const handleToggleActive = async (section) => {
    try {
      await updateSection(section.id, { active: !section.active })
    } catch (err) {
      alert(`Failed to toggle: ${err.message}`)
    }
  }

  const handleDelete = async (section) => {
    if (!confirm(`Delete section "${section.name}"?`)) return
    try {
      await deleteSection(section.id)
    } catch (err) {
      alert(`Failed to delete: ${err.message}`)
    }
  }

  const startEdit = (section) => {
    setEditingId(section.id)
    setEditName(section.name)
    setEditColor(section.color)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditName('')
    setEditColor('')
  }

  const handleDragStart = (e, section) => {
    setDraggedId(section.id)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e, section) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverId(section.id)
  }

  const handleDragLeave = () => {
    setDragOverId(null)
  }

  const handleDrop = async (e, targetSection) => {
    e.preventDefault()
    if (!draggedId || draggedId === targetSection.id) {
      setDraggedId(null)
      setDragOverId(null)
      return
    }

    setIsSorting(true)

    const draggedIndex = sections.findIndex(s => s.id === draggedId)
    const targetIndex = sections.findIndex(s => s.id === targetSection.id)

    if (draggedIndex === -1 || targetIndex === -1) {
      setIsSorting(false)
      return
    }

    // Create new array with swapped positions
    const newSections = [...sections]
    const [movedSection] = newSections.splice(draggedIndex, 1)
    newSections.splice(targetIndex, 0, movedSection)

    // Update sort orders
    try {
      for (let i = 0; i < newSections.length; i++) {
        await updateSection(newSections[i].id, { sort_order: i + 1 })
      }
    } catch (err) {
      alert(`Failed to reorder: ${err.message}`)
    } finally {
      setIsSorting(false)
    }

    setDraggedId(null)
    setDragOverId(null)
  }

  const displaySections = sections.length > 0 ? sections : []

  return (
    <div className={`p-6 space-y-6 transition-colors duration-300 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
      <div>
        <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-slate-200' : 'text-gray-900'}`}>Task Sections</h2>
        <p className={`mb-6 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Manage task categories used across the system</p>
      </div>

      {/* Add New Section */}
      <div className={`p-4 rounded border transition-colors duration-300 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-blue-50 border-blue-200'}`}>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Section Name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g., Claims"
              className="input-field w-full"
            />
          </div>
          <div className="w-24">
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Color</label>
            <input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              className="w-full h-10 cursor-pointer"
            />
          </div>
          <button
            onClick={handleCreate}
            className="btn-primary px-4 py-2"
          >
            Add Section
          </button>
        </div>
      </div>

      {/* Sections Table */}
      <div className={`overflow-hidden rounded border transition-colors duration-300 relative ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        {isSorting && (
          <div className={`absolute inset-0 z-10 flex items-center justify-center rounded ${isDarkMode ? 'bg-slate-800/90' : 'bg-white/90'}`}>
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin h-8 w-8 border-4 border-sfp-orange border-t-transparent rounded-full" />
              <p className={`text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-sfp-darkgray'}`}>Updating sort order...</p>
            </div>
          </div>
        )}
        <table className={`w-full ${isSorting ? 'opacity-50 pointer-events-none' : ''}`}>
          <thead className={`border-b transition-colors duration-300 ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
            <tr>
              <th className={`px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}></th>
              <th className={`px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Color</th>
              <th className={`px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Name</th>
              <th className={`px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Sort Order</th>
              <th className={`px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Active</th>
              <th className={`px-6 py-3 text-right text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className={`px-6 py-4 text-center ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Loading...</td>
              </tr>
            ) : sections.length === 0 ? (
              <tr>
                <td colSpan="5" className={`px-6 py-4 text-center ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>No sections found</td>
              </tr>
            ) : (
              displaySections.map((section) => (
                <tr
                  key={section.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, section)}
                  onDragOver={(e) => handleDragOver(e, section)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, section)}
                  className={`border-b transition-all duration-200 cursor-move ${
                    draggedId === section.id ? 'opacity-50' : ''
                  } ${
                    dragOverId === section.id ? (isDarkMode ? 'bg-slate-600' : 'bg-orange-100') : ''
                  } ${
                    isDarkMode ? 'border-slate-700 hover:bg-slate-700' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <td className={`px-6 py-4 text-lg font-bold ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                    ⋮⋮
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-6 h-6 rounded border border-gray-300"
                        style={{ backgroundColor: section.color }}
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {editingId === section.id ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="input-field"
                      />
                    ) : (
                      <span className={`font-medium ${isDarkMode ? 'text-slate-200' : 'text-gray-900'}`}>{section.name}</span>
                    )}
                  </td>
                  <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    {editingId === section.id ? (
                      <input
                        type="number"
                        defaultValue={section.sort_order}
                        readOnly
                        className="input-field w-20"
                      />
                    ) : (
                      section.sort_order
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggleActive(section)}
                      className={`px-3 py-1 rounded text-sm font-medium transition ${
                        section.active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {section.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {editingId === section.id ? (
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => handleEdit(section)}
                          className="text-green-600 hover:text-green-700 font-semibold"
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-gray-500 hover:text-gray-700 font-semibold"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => startEdit(section)}
                          className="text-sfp-orange hover:text-orange-700 font-semibold text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(section)}
                          className="text-red-500 hover:text-red-700 font-semibold text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
